// packages/shared/src/constants/ptFunctionalDefaults.ts
// Default functional assessment catalog items for Physical Therapy.
// Covers geriatric/rehab (assist level scale) and active adult/sports
// (movement quality scale) populations.

import type { FlowSheetItemCatalogEntry } from '../types/models/flowSheet';

const ts = 0; // placeholder timestamps for defaults

/** Helper to build a functional catalog entry */
function entry(
  id: string,
  label: string,
  data_type: FlowSheetItemCatalogEntry['data_type'],
  subcategory: string,
  opts: Partial<FlowSheetItemCatalogEntry> = {}
): FlowSheetItemCatalogEntry {
  return {
    id,
    organization_id: 'system',
    label,
    data_type,
    category: 'functional',
    subcategory,
    is_active: true,
    created_at: ts,
    updated_at: ts,
    ...opts,
  };
}

// ── Assist Level Scale ──────────────────────────────────────────────────
// Simple 6-level scale — most common in outpatient PT documentation.
// Alternative: FIM 1-7 scale (Total Assist → Max Assist → Mod Assist →
// Min Assist → Supervision → Modified Independent → Independent).
// Orgs preferring FIM-style can override via custom catalog items.
const ASSIST_LEVEL_OPTIONS = [
  'Independent', 'Modified Independent', 'Set up/Clean up', 'Supervision/Verbal cueing',
  'Contact guard assistance', 'Minimal assistance', 'Moderate assistance', 'Maximum assistance',
  'Dependent', 'Not applicable', 'Unknown',
];

// ── Movement Quality Scale ──────────────────────────────────────────────
const MOVEMENT_QUALITY_OPTIONS = [
  'Normal', 'Mild Deviation', 'Moderate Deviation', 'Severe Deviation', 'Unable', 'Not Tested',
];

const PASS_FAIL_OPTIONS = ['Pass', 'Fail', 'Not Tested'];

// ── Geriatric / Rehab Items ─────────────────────────────────────────────

const BED_MOBILITY: FlowSheetItemCatalogEntry[] = [
  entry('func_supine_to_sit', 'Supine to Sit', 'select', 'Bed Mobility', { select_options: ASSIST_LEVEL_OPTIONS }),
  entry('func_sit_to_supine', 'Sit to Supine', 'select', 'Bed Mobility', { select_options: ASSIST_LEVEL_OPTIONS }),
  entry('func_rolling', 'Rolling', 'select', 'Bed Mobility', { select_options: ASSIST_LEVEL_OPTIONS, bilateral: true }),
];

const TRANSFERS: FlowSheetItemCatalogEntry[] = [
  entry('func_sit_to_stand', 'Sit to Stand', 'select', 'Transfers', { select_options: ASSIST_LEVEL_OPTIONS }),
  entry('func_bed_to_wc', 'Bed to Wheelchair', 'select', 'Transfers', { select_options: ASSIST_LEVEL_OPTIONS }),
  entry('func_toilet_xfer', 'Toilet Transfer', 'select', 'Transfers', { select_options: ASSIST_LEVEL_OPTIONS }),
  entry('func_tub_shower_xfer', 'Tub/Shower Transfer', 'select', 'Transfers', { select_options: ASSIST_LEVEL_OPTIONS }),
  entry('func_car_xfer', 'Car Transfer', 'select', 'Transfers', { select_options: ASSIST_LEVEL_OPTIONS }),
  entry('func_floor_to_stand', 'Floor to Standing', 'select', 'Transfers', { select_options: ASSIST_LEVEL_OPTIONS }),
  entry('func_stand_to_floor', 'Standing to Floor', 'select', 'Transfers', { select_options: ASSIST_LEVEL_OPTIONS }),
];

const AMBULATION: FlowSheetItemCatalogEntry[] = [
  entry('func_amb_level', 'Ambulation — Level Surface', 'select', 'Ambulation', { select_options: ASSIST_LEVEL_OPTIONS }),
  entry('func_amb_distance', 'Ambulation Distance', 'numeric', 'Ambulation', { unit: 'ft' }),
  entry('func_amb_community', 'Community Ambulation', 'select', 'Ambulation', { select_options: ASSIST_LEVEL_OPTIONS }),
  entry('func_amb_uneven', 'Ambulation — Uneven Surface', 'select', 'Ambulation', { select_options: ASSIST_LEVEL_OPTIONS }),
  entry('func_assistive_device', 'Assistive Device Used', 'select', 'Ambulation', {
    select_options: ['None', 'SPC', 'SBQC', 'LBQC', 'Standard Walker', 'Rollator', 'Axillary Crutches', 'Lofstrand Crutches', 'Other'],
  }),
  entry('func_wb_status', 'Weight Bearing Status', 'select', 'Ambulation', {
    select_options: ['WBAT', 'FWB', 'PWB', 'TTWB', 'NWB', 'Other'],
  }),
];

const STAIRS: FlowSheetItemCatalogEntry[] = [
  entry('func_stair_ascent', 'Stair Ascent', 'select', 'Stairs', { select_options: ASSIST_LEVEL_OPTIONS }),
  entry('func_stair_descent', 'Stair Descent', 'select', 'Stairs', { select_options: ASSIST_LEVEL_OPTIONS }),
  entry('func_rail_use', 'Railing Use', 'select', 'Stairs', {
    select_options: ['No Rail', 'One Rail', 'Two Rails', 'Rail + Assist', 'Not Attempted'],
  }),
];

const BALANCE_FUNC: FlowSheetItemCatalogEntry[] = [
  entry('func_static_stand_bal', 'Static Standing Balance', 'select', 'Balance', {
    select_options: ['Normal', 'Good', 'Fair', 'Poor', 'Unable'],
  }),
  entry('func_dynamic_stand_bal', 'Dynamic Standing Balance', 'select', 'Balance', {
    select_options: ['Normal', 'Good', 'Fair', 'Poor', 'Unable'],
  }),
];

// ── Active Adult / Sports Items ─────────────────────────────────────────

const MOVEMENT_QUALITY: FlowSheetItemCatalogEntry[] = [
  entry('func_squat_bilateral', 'Bilateral Squat', 'select', 'Movement Quality', { select_options: MOVEMENT_QUALITY_OPTIONS, body_regions: ['hip', 'knee'] }),
  entry('func_squat_single_leg', 'Single Leg Squat', 'select', 'Movement Quality', { select_options: MOVEMENT_QUALITY_OPTIONS, bilateral: true, body_regions: ['hip', 'knee'] }),
  entry('func_squat_overhead', 'Overhead Squat', 'select', 'Movement Quality', { select_options: MOVEMENT_QUALITY_OPTIONS, body_regions: ['shoulder', 'hip', 'knee'] }),
  entry('func_lunge_forward', 'Forward Lunge', 'select', 'Movement Quality', { select_options: MOVEMENT_QUALITY_OPTIONS, bilateral: true, body_regions: ['hip', 'knee'] }),
  entry('func_lunge_lateral', 'Lateral Lunge', 'select', 'Movement Quality', { select_options: MOVEMENT_QUALITY_OPTIONS, bilateral: true, body_regions: ['hip', 'knee'] }),
  entry('func_step_down_forward', 'Forward Step-Down', 'select', 'Movement Quality', { select_options: MOVEMENT_QUALITY_OPTIONS, bilateral: true, body_regions: ['hip', 'knee'] }),
  entry('func_single_leg_rdl', 'Single Leg RDL', 'select', 'Movement Quality', { select_options: MOVEMENT_QUALITY_OPTIONS, bilateral: true, body_regions: ['hip', 'knee'] }),
];

const HOP_TESTS: FlowSheetItemCatalogEntry[] = [
  entry('func_hop_single', 'Single Leg Hop for Distance', 'numeric', 'Hop Tests', { unit: 'cm', bilateral: true, body_regions: ['knee', 'ankle'] }),
  entry('func_hop_triple', 'Triple Hop for Distance', 'numeric', 'Hop Tests', { unit: 'cm', bilateral: true, body_regions: ['knee', 'ankle'] }),
  entry('func_hop_crossover', 'Crossover Hop for Distance', 'numeric', 'Hop Tests', { unit: 'cm', bilateral: true, body_regions: ['knee', 'ankle'] }),
  entry('func_hop_6m_timed', '6m Timed Hop', 'numeric', 'Hop Tests', { unit: 'sec', bilateral: true, body_regions: ['knee', 'ankle'] }),
];

const CORE_UE: FlowSheetItemCatalogEntry[] = [
  entry('func_plank_front', 'Front Plank Hold', 'numeric', 'Core & UE', { unit: 'sec', body_regions: ['lumbar'] }),
  entry('func_plank_side', 'Side Plank Hold', 'numeric', 'Core & UE', { unit: 'sec', bilateral: true, body_regions: ['lumbar'] }),
  entry('func_pushup', 'Push-Up Assessment', 'select', 'Core & UE', { select_options: MOVEMENT_QUALITY_OPTIONS, body_regions: ['shoulder'] }),
];

const RUNNING: FlowSheetItemCatalogEntry[] = [
  entry('func_run_quality', 'Running Gait — Overall Quality', 'select', 'Running', { select_options: MOVEMENT_QUALITY_OPTIONS }),
  entry('func_run_cadence', 'Running Cadence', 'numeric', 'Running', { unit: 'steps/min' }),
];

const RETURN_TO_SPORT: FlowSheetItemCatalogEntry[] = [
  entry('func_rts_running', 'Return to Running Clearance', 'select', 'Return to Sport', { select_options: PASS_FAIL_OPTIONS }),
  entry('func_rts_sport', 'Return to Sport Clearance', 'select', 'Return to Sport', { select_options: PASS_FAIL_OPTIONS }),
];

// ── Full functional defaults ────────────────────────────────────────────

export const PT_FUNCTIONAL_DEFAULTS: FlowSheetItemCatalogEntry[] = [
  ...BED_MOBILITY,
  ...TRANSFERS,
  ...AMBULATION,
  ...STAIRS,
  ...BALANCE_FUNC,
  ...MOVEMENT_QUALITY,
  ...HOP_TESTS,
  ...CORE_UE,
  ...RUNNING,
  ...RETURN_TO_SPORT,
];
